import React from "react";
import {
    Col,
    Input,
    Row,
    Checkbox,
    Form,
    InputNumber,
} from "antd";
import { useSelector } from "react-redux";
import { SEAT_LABEL_OPTIONS } from '../../../enums/SeatDistributionEnum'
import { tPropertyBar, tRow } from "../../../assets/libs/i18n/translator";
import { CategorySelect } from "./CategorySelect";
import { DistributionSelect } from "./DistributionSelect";
import { StartsAtInput } from "./StartsAtInput";

const RowPropertiesPanel = ({ form, getFieldValue, selectedItem }) => {
    const categories = useSelector((state) => state.properties.categories);
    const { Item } = Form;

    // Read the currently selected distribution from the form so the startsAt
    // input reflects the choice immediately, before it is persisted on the item.
    const seatDistribution =
        getFieldValue?.('seatLabeling') ?? selectedItem.seat.key;

    return (
        <>
            <Row>
                <Col span={24} className="mb-1">
                    <h3 className="text-center">{tRow("rows")}</h3>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1">
                    <h3><b>{tPropertyBar("category")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item wrapperCol={{ span: 24 }}>
                        {form('rowCategory', {
                            initialValue: selectedItem.category.key,
                        })(
                            <CategorySelect categories={categories} />
                        )}
                    </Item>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("seats")}</b></h3>
                </Col>
                <Col span={24}>
                    <Item
                        label={tPropertyBar("seatsQuantity")}
                        labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}
                    >
                        {form('seatsQuantity', {
                            initialValue: selectedItem.seatsQuantity,
                        })(<InputNumber min={1} />)}
                    </Item>
                    <Item
                        label={tRow("seatsSpacing")}
                        labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}
                    >
                        {form('seatsSpacing', {
                            initialValue: selectedItem.seatsSpacing,
                        })(<InputNumber min={0} />)}
                    </Item>
                    <Item
                        label={tRow("curve")}
                        labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}
                    >
                        {form('seatsCurve', {
                            initialValue: selectedItem.seatsCurve ?? 0,
                        })(<InputNumber min={-30} max={30} />)}
                    </Item>
                </Col>
                <Col span={24} className="mb-1 mt-2"><h3><b>{tPropertyBar("sectionLabeling")}</b></h3></Col>
                <Col span={24}>
                    <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form('sectionLabel', {
                            initialValue: selectedItem.section.label,
                            disabled: selectedItem.zoneID
                        })(<Input disabled={selectedItem.zoneID} />)}
                    </Item>
                    <Item label={tPropertyBar("displayedLabel")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form('displayedSectionLabel', {
                            initialValue: selectedItem.section.displayedLabel,
                        })(<Input disabled={selectedItem.zoneID} />)}
                    </Item>
                </Col>
                <Col span={24} className="mb-1 mt-2"><h3><b>{tRow("rowLabeling")}</b></h3></Col>
                <Col span={24}>
                    <Item label={tRow("active")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form('enabledRowLabel', {
                            rules: [{ type: 'boolean', },],
                            valuePropName: 'checked',
                            initialValue: selectedItem.row.enabled
                        })(
                            <Checkbox />)}
                    </Item>
                    {selectedItem.row.enabled && (
                        <>
                            <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form('rowLabel', {
                                    initialValue: selectedItem.row.label,
                                })(<Input />)}
                            </Item>
                            <Item label={tPropertyBar("displayedLabel")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form('displayedRowLabel', {
                                    initialValue: selectedItem.row.displayedLabel,
                                })(<Input />)}
                            </Item>
                            <Row>
                                <Col span={24} xs={13}>
                                    <Item label={tRow("position")} labelCol={{ span: 22 }} wrapperCol={{ span: 1 }}>
                                        {form('labelPositionLeft', {
                                            rules: [{ type: 'boolean', },],
                                            valuePropName: 'checked',
                                            initialValue: selectedItem.row.position.left
                                        })(
                                            <Checkbox />)}
                                    </Item>
                                </Col>
                                <Col xs={4} offset={1}>
                                    <span
                                        className="icon-tool-seats"
                                        style={{ fontSize: "10px" }}
                                    ></span>
                                </Col>
                                <Col xs={3}>
                                    <Item labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                        {form('labelPositionRight', {
                                            rules: [{ type: 'boolean', },],
                                            valuePropName: 'checked',
                                            initialValue: selectedItem.row.position.right
                                        })(
                                            <Checkbox />)}
                                    </Item>
                                </Col>
                            </Row>
                        </>

                    )

                    }

                </Col>
                <Col span={24} className="mb-1 mt-2"><h3><b>{tPropertyBar("seatLabeling")}</b></h3></Col>
                <Col span={24}>
                    <Item label={tPropertyBar("distribution")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form('seatLabeling', { initialValue: selectedItem.seat.key })(
                            <DistributionSelect distributionList={SEAT_LABEL_OPTIONS} />
                        )}
                    </Item>
                    {seatDistribution !== 0 &&
                        <>
                            <Item label={tPropertyBar("startsAt")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form('startsAt', { initialValue: selectedItem.seat.startsAt })(
                                    <StartsAtInput distributionKey={seatDistribution} />
                                )}
                            </Item>
                            <Item label={tRow("reverse")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                                {form('reverse', {
                                    initialValue: selectedItem.seat.reverse,
                                    valuePropName: 'checked',
                                })(
                                    <Checkbox />
                                )}
                            </Item>
                        </>
                    }

                </Col>
            </Row>
        </>
    );
}

export { RowPropertiesPanel };
